import { Link, useFetcher } from '@remix-run/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Text from '~/components/Text';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useToast from '~/hooks/useToast';

import usePublication from '~/hooks/usePublication';
import LoginWithPassword from '../Forms/LoginWithPassword';
import RedirectToHiddenInput from '../Forms/RedirectToHiddenInput';

interface LoginActionProps {
  onSuccess?: () => void;
  bypassSparkLoopMode?: boolean;
  showLoginWithPassword?: boolean;
}

const LoginWithPasswordAction = ({
  onSuccess,
  bypassSparkLoopMode = false,
  showLoginWithPassword = false,
}: LoginActionProps) => {
  const isLoggedIn = useIsLoggedIn();
  const { one_time_password_enabled } = usePublication();
  const fetcher = useFetcher<{
    toast: any;
  }>();
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const { t } = useTranslation();

  /**
   * There was a weird bug on a users pub that was not clearing the signup redirects after the final step.
   * This was not happening with other users and was not reproducible locally or on staging. It may have to do with script that was added to the page.
   *
   * Either way, the bug basically forced the final redirect to happen again on the next login attempt. So this is a quick fix to clear the signup redirects
   * before that can happen.
   */
  useEffect(() => {
    localStorage.removeItem('signup_redirects');
  }, []);

  useToast(fetcherData?.toast, onSuccess);

  if (isLoggedIn) {
    return null;
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    const loginEmail = formData.get('login_email');
    if (bypassSparkLoopMode && loginEmail) {
      formData.append('email', loginEmail);
    }

    fetcher.submit(formData, { method: 'post', action: '/login' });

    return;
  };

  return (
    <>
      <fetcher.Form method="post" action="/login" className="w-full" onSubmit={(e) => handleFormSubmit(e)}>
        <RedirectToHiddenInput />
        <div className="flex flex-col w-full space-y-2 sm:space-y-6">
          {bypassSparkLoopMode ? (
            <LoginWithPassword
              emailFieldName="login_email"
              emailFieldType="text"
              isSubmitting={isSubmitting}
              useMagicLink={!one_time_password_enabled}
              useOneTimePassword={one_time_password_enabled}
              showLoginWithPassword={showLoginWithPassword}
            />
          ) : (
            <>
              <LoginWithPassword
                isSubmitting={isSubmitting}
                useMagicLink={!one_time_password_enabled}
                useOneTimePassword={one_time_password_enabled}
                showLoginWithPassword={showLoginWithPassword}
              />
              <div className="flex flex-col">
                <Text as="p" size="xs" type="body" theme={'default'}>
                  {t('login.sign_up_label')}{' '}
                  <Link to="/subscribe" className="underline">
                    <Text as="span" size="xs" type="body" weight="bold" theme={'default'}>
                      {t('shared.cta.sign_up')}
                    </Text>
                  </Link>
                </Text>
              </div>
            </>
          )}
        </div>
      </fetcher.Form>
    </>
  );
};

export default LoginWithPasswordAction;
